import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import FilterBar, { type FilterGroup } from '@/components/feature/FilterBar';
import SortTabs from '@/components/feature/SortTabs';
import VideoCard from '@/components/feature/VideoCard';
import { categories, perspectives, regions, years } from '@/mocks/categories';
import { filterVideos, getPopular, sortVideos, type SortKey } from '@/lib/videos';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 9;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get('q') || '').trim();
  const [input, setInput] = useState(keyword);
  const [category, setCategory] = useState('');
  const [region, setRegion] = useState('');
  const [perspective, setPerspective] = useState('');
  const [year, setYear] = useState('');
  const [sort, setSort] = useState<SortKey>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 无码中文字幕` : '搜索与筛选 - 无码中文字幕',
    description: keyword
      ? `无码中文字幕中与“${keyword}”相关的日韩自拍视频搜索结果，支持题材、地区、视角与年份叠加筛选。`
      : '按关键词搜索日韩自拍视频，支持题材、地区、视角与年份叠加筛选，快速定位想看的内容。',
    keywords: `${keyword || '视频搜索'},日韩自拍视频,在线观看,无码中文字幕`,
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [keyword, category, region, perspective, year, sort]);

  const result = useMemo(() => {
    const filtered = filterVideos({ keyword, category, region, perspective, year });
    return sortVideos(filtered, sort);
  }, [keyword, category, region, perspective, year, sort]);

  const activeCount = [category, region, perspective, year].filter(Boolean).length;
  const hot = getPopular(4);

  const groups: FilterGroup[] = [
    { key: 'category', label: '题材', options: categories.map((item) => item.name), value: category },
    { key: 'region', label: '地区', options: regions, value: region },
    { key: 'perspective', label: '视角', options: perspectives, value: perspective },
    { key: 'year', label: '年份', options: years.map(String), value: year },
  ];

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'category') setCategory(value);
    if (key === 'region') setRegion(value);
    if (key === 'perspective') setPerspective(value);
    if (key === 'year') setYear(value);
  };

  const resetAll = () => {
    setCategory('');
    setRegion('');
    setPerspective('');
    setYear('');
    setSort('latest');
    setSearchParams({});
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-primary-700">
              首页
            </Link>
            <span className="flex h-3 w-3 items-center justify-center text-background-400">
              <i className="ri-arrow-right-s-line text-xs" />
            </span>
            <span className="text-foreground-800">搜索</span>
          </nav>

          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            {keyword ? `搜索：${keyword}` : '搜索与筛选'}
          </h1>
          <p className="mt-2 text-[13px] text-foreground-500">
            支持标题、题材、标签与简介的全文匹配，可叠加题材、地区、视角与年份筛选。
          </p>

          <form onSubmit={submit} className="mt-5 flex w-full max-w-[680px] flex-col gap-2 sm:flex-row">
            <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 transition-colors focus-within:border-primary-400">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="search"
                name="q"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入关键词，例如：温泉、韩国、校园"
                className="h-full w-full border-none bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {input && (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => setInput('')}
                  className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-background-200 text-foreground-500"
                >
                  <i className="ri-close-line text-xs" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="flex h-11 shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-600 px-6 text-sm text-background-50 transition-colors hover:bg-primary-700"
            >
              搜索
            </button>
          </form>

          {!keyword && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-400">热门搜索</span>
              {['温泉', '韩国', '校园', '夜间街景', '素人'].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setSearchParams({ q: item })}
                  className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  {item}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <FilterBar groups={groups} onChange={handleFilterChange} onReset={resetAll} activeCount={activeCount} />

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[12px] text-foreground-500">
            共 <span className="font-medium text-foreground-900">{result.length}</span> 条匹配结果
            {keyword && (
              <>
                {' '}
                · 关键词 <span className="text-primary-700">“{keyword}”</span>
              </>
            )}
          </p>
          <SortTabs value={sort} onChange={setSort} />
        </div>

        {result.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-1 gap-x-3 gap-y-6 sm:grid-cols-2 md:gap-x-5 md:gap-y-8 lg:grid-cols-3">
              {result.slice(0, visible).map((video, index) => (
                <VideoCard key={video.id} video={video} index={index} showTags />
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-2">
              <p className="text-[11px] text-foreground-400">
                已显示 {Math.min(visible, result.length)} / {result.length} 条
              </p>
              {visible < result.length && (
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="flex h-10 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 text-[13px] text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-down-double-line text-sm" />
                  </span>
                  加载更多
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="mt-6">
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
                <i className="ri-search-line text-xl" />
              </span>
              <h2 className="mt-4 text-base font-medium text-foreground-900">没有找到匹配的内容</h2>
              <p className="mt-2 max-w-[400px] text-[12px] leading-6 text-foreground-500">
                换个关键词试试，或者重置筛选条件浏览全部条目。当前筛选条件下共 0 条结果。
              </p>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={resetAll}
                  className="flex h-10 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 text-[13px] text-background-50 transition-colors hover:bg-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-refresh-line text-sm" />
                  </span>
                  重置筛选
                </button>
                <Link
                  to="/"
                  className="flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-5 text-[13px] text-foreground-700 transition-colors hover:bg-background-50"
                >
                  返回首页
                </Link>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-end justify-between">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">也许你想看这些</h2>
                <Link to="/search" className="text-[12px] text-foreground-500 transition-colors hover:text-primary-700">
                  浏览全部
                </Link>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-6 md:grid-cols-4 md:gap-x-5">
                {hot.map((video, index) => (
                  <VideoCard key={video.id} video={video} index={index} />
                ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}